import { Component, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { AdmytService } from '../../../services/admyt.service'
import { ParkingService } from '../../../services/parking.service'
import { ClientService } from '../../../services/client.service'
import { Parking, ParkingStatus } from '@parkupp/core'
import { Location } from '@angular/common'
import Swal from 'sweetalert2'

interface AdmytCenter {
    id: string
    name: string
    zones: string[]
}

interface AdmytTenant {
    id: string
    name: string
    zones: string[]
}

interface AdmytTenant {
    id: string
    name: string
    centre: number
    baysNumber: number
    status: string
    createdAt: string
}
interface AdmytTenantResponse {
    data: AdmytTenant[]
    total: number
}

@Component({
    selector: 'app-admin-view-admyt-center',
    templateUrl: './admin-view-admyt-center.component.html',
    styleUrls: ['./admin-view-admyt-center.component.scss'],
})
export class AdminViewAdmytCenterComponent implements OnInit {
    centerId: string
    center: AdmytCenter | null = null
    relatedParkings: Parking[] = []
    relatedTenants: AdmytTenant[] = []
    loading = true
    error: string | null = null
    parkingsLoading = false
    tenantsLoading = false

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private admytService: AdmytService,
        private parkingService: ParkingService,
        private clientService: ClientService
    ) {}

    async ngOnInit() {
        this.centerId = this.route.snapshot.params['centerId']
        await this.loadCenterDetails()
        await this.loadRelatedParkings()
        await this.loadRelatedTenants()
    }

    async loadCenterDetails() {
        try {
            this.loading = true
            this.error = null

            // Fetch all centers and find the specific one
            const centersResult = await this.admytService.getCentres()
            console.log(centersResult)
            const allCenters: AdmytCenter[] = (centersResult.data as any) || []

            this.center = allCenters.find((center) => center.id == this.centerId) || null

            if (!this.center) {
                this.error = `Center with ID "${this.centerId}" not found.`
            }

            this.loading = false
        } catch (error) {
            console.error('Failed to load center details:', error)
            this.error = 'Failed to load center details. Please try again.'
            this.loading = false
        }
    }

    async loadRelatedParkings() {
        try {
            this.parkingsLoading = true

            // Fetch all parkings and filter by center ID
            const allParkings = await this.parkingService.listPromise({ status: ParkingStatus.PUBLISHED })
            this.relatedParkings = allParkings.filter((parking) => parking.admytCenter == this.centerId)

            this.parkingsLoading = false
        } catch (error) {
            console.error('Failed to load related parkings:', error)
            this.parkingsLoading = false
        }
    }

    async loadRelatedTenants() {
        try {
            this.tenantsLoading = true

            // Fetch tenants for this center
            console.log('🔍 Loading tenants for center ID:', this.centerId)
            const tenantsResult = await this.admytService.getTenants(this.centerId)
            console.log('🔍 Raw tenants API response:', tenantsResult)

            const tenantsresultData = tenantsResult.data as any
            console.log('🔍 Tenants result data:', tenantsresultData)

            // Check if data is nested or direct
            if (tenantsresultData && tenantsresultData.data) {
                console.log('🔍 Using nested data structure')
                this.relatedTenants = tenantsresultData.data as AdmytTenant[]
            } else if (Array.isArray(tenantsresultData)) {
                console.log('🔍 Using direct array structure')
                this.relatedTenants = tenantsresultData as AdmytTenant[]
            } else {
                console.log('🔍 Unexpected data structure, trying to use as is')
                this.relatedTenants = tenantsresultData as AdmytTenant[]
            }

            console.log('🔍 Final related tenants:', this.relatedTenants)

            this.tenantsLoading = false
        } catch (error) {
            console.error('Failed to load related tenants:', error)
            this.tenantsLoading = false
        }
    }

    viewParking(parking: Parking) {
        this.router.navigate(['/parking', parking.$key])
    }

    editParking(parking: Parking) {
        this.router.navigate(['/merchant', parking.merchantRef?.id, 'parking', parking.$key])
    }

    goBack() {
        this.location.back()
    }

    goToCentersList() {
        this.router.navigate(['/admin/admyt-centers'])
    }

    async refreshData() {
        await this.loadCenterDetails()
        await this.loadRelatedParkings()
        await this.loadRelatedTenants()
    }

    async showCreateTenantUserModal() {
        const { value: email } = await Swal.fire({
            title: 'Add Tenant User to Center',
            html: `
                <div class="text-start">
                    <p class="mb-3 text-muted">Enter the email address of an existing ParkUpp user to add them to this Admyt center.</p>
                    <div class="mb-3">
                        <label for="swal-email" class="form-label">User Email Address</label>
                        <input id="swal-email" class="form-control" type="email" placeholder="Enter existing user's email address" required>
                    </div>
                    <div class="mb-3">
                        <label for="swal-bays" class="form-label">Number of Bays</label>
                        <input id="swal-bays" class="form-control" type="number" min="1" value="1" required>
                    </div>
                    <small class="text-muted">
                        <i class="fa fa-info-circle me-1"></i>
                        The system will automatically use the user's existing details and vehicles.
                    </small>
                </div>
            `,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Add User to Center',
            cancelButtonText: 'Cancel',
            preConfirm: () => {
                const emailInput = (document.getElementById('swal-email') as HTMLInputElement).value
                const baysInput = parseInt((document.getElementById('swal-bays') as HTMLInputElement).value)

                if (!emailInput || !baysInput) {
                    Swal.showValidationMessage('Please fill in all fields')
                    return false
                }

                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput)) {
                    Swal.showValidationMessage('Please enter a valid email address')
                    return false
                }

                return { email: emailInput, baysNumber: baysInput }
            },
        })

        if (email) {
            await this.createTenantUserFromEmail(email.email, email.baysNumber)
        }
    }

    async createTenantUserFromEmail(email: string, baysNumber: number) {
        try {
            console.log('Creating tenant user by email:', email, 'for center:', this.centerId)

            // Call the backend function that handles all the logic
            const result = await this.admytService.createTenantUserByEmail(email, this.centerId, baysNumber)
            console.log('Backend result:', result)

            const resultData = result.data as any
            if (resultData.success) {
                // Refresh the tenants list
                await this.loadRelatedTenants()

                Swal.fire({
                    title: 'Success!',
                    text: `User "${resultData.userDetails.firstName} ${resultData.userDetails.lastName}" has been added to this center successfully with ${resultData.vehiclesAdded} vehicle(s).`,
                    icon: 'success',
                })
            } else {
                throw new Error('Backend function returned unsuccessful result')
            }
        } catch (error: any) {
            console.error('Failed to create tenant user from email:', error)

            let errorMessage = 'Failed to add user to center. Please try again.'
            if (error?.message) {
                errorMessage = error.message
            }

            Swal.fire({
                title: 'Error',
                text: errorMessage,
                icon: 'error',
            })
        }
    }

    /**
     * Parse subscription ID from tenant name
     * Tenant names follow pattern: "{FirstName} {LastName} {subscriptionId}"
     */
    getSubscriptionFromTenantName(name: string): string | null {
        if (!name) return null
        const parts = name.split(' ')
        if (parts.length >= 3) {
            return parts[parts.length - 1]
        }
        return null
    }

    /**
     * Get status class for styling
     */
    getStatusClass(status: string): string {
        switch (status?.toLowerCase()) {
            case 'active':
                return 'admyt-status admyt-status--active'
            case 'inactive':
                return 'admyt-status admyt-status--inactive'
            case 'pending':
                return 'admyt-status admyt-status--pending'
            case 'published':
                return 'admyt-status admyt-status--active'
            case 'disabled':
                return 'admyt-status admyt-status--error'
            default:
                return 'admyt-status admyt-status--inactive'
        }
    }

    async deleteTenant(tenantId: string) {
        const tenant = this.relatedTenants.find((t) => t.id === tenantId)
        const tenantName = tenant?.name || 'this tenant'

        if (!confirm(`Are you sure you want to delete "${tenantName}"?\n\nThis will permanently deactivate the tenant in Admyt and cannot be undone.`)) {
            return
        }

        try {
            console.log('Deactivating tenant:', tenantId)
            await this.admytService.deactivateTenant(this.centerId, tenantId)

            // Remove the tenant from the local list
            this.relatedTenants = this.relatedTenants.filter((tenant) => tenant.id !== tenantId)

            alert(`Tenant "${tenantName}" has been successfully deleted from Admyt.`)
            console.log('Tenant deactivated successfully')
        } catch (error: any) {
            console.error('Failed to deactivate tenant:', error)
            alert(`Failed to delete tenant "${tenantName}". Please try again.\n\nError: ${error.message || 'Unknown error'}`)
        }
    }

    /**
     * Truncate any value (string or number) to specified length
     */
    truncate(value: any, maxLength: number): string {
        if (value === null || value === undefined) return ''
        const str = String(value)
        if (str.length <= maxLength) return str
        return str.substring(0, maxLength) + '...'
    }
}
